import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Modal } from '@xsolla/ui-modal';

import { getUserAttributes } from '../../../api';
import { UserAttributes } from '../../../api/users/attributes/types';
import { useConfig } from '../../../contexts/config';
import { useTableData } from '../../../hooks';
import { getRandomString } from '../../../utils';
import { Preloader } from '../../ui';
import { AttributesTable } from './AttributesTable';
import AttributesErrorMessage from './ErrorMessage';
import Footer from './Footer';
import Header from './Header';
import { SortData, UserData } from './types';

type Props = {
  onClose: () => void;
  userData: UserData;
  projectId: string;
};

const AttributesModal = ({ onClose, userData, projectId }: Props): JSX.Element => {
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();

  const { data, setData, fetching, error } = useTableData<UserAttributes>(
    () =>
      getUserAttributes(projectId, userData.id).then((attributes) =>
        attributes.map((attr) => ({ ...attr, tableKey: getRandomString() }))
      ),
    1000,
    {
      order_column: 'description'
    }
  );

  const [currSort, setCurrSort] = useState<SortData>({ key: 'description', direction: 1 });
  const [currFilterData, setCurrFilterData] = useState<UserAttributes | null>(null);

  useEffect(() => {
    if (data) {
      setCurrFilterData(data.length ? data : null);
    }
  }, [data]);

  const sortFunction = useCallback(
    (key: 'key' | 'description' | 'data_type' | 'attr_type') => {
      if (!data) return;

      if (key === currSort.key) {
        const newDirection = currSort.direction === 1 ? -1 : 1;

        const newData = data.sort((a, b) => {
          if (typeof a[key] === 'string' && typeof b[key] === 'string') {
            return (
              newDirection * a[key].localeCompare(b[key], [intl.locale], { caseFirst: 'upper' })
            );
          }

          if (typeof a[key] !== 'string') {
            return newDirection;
          }

          if (typeof b[key] !== 'string') {
            return newDirection * -1;
          }

          return newDirection * a[key].localeCompare(b[key], [intl.locale], { caseFirst: 'upper' });
        });
        setData([...newData]);
        setCurrSort({ key, direction: newDirection });
        return;
      }

      const newData = data.sort((a, b) => {
        if (typeof a[key] === 'string' && typeof b[key] === 'string') {
          return a[key].localeCompare(b[key], [intl.locale], { caseFirst: 'upper' });
        }

        if (typeof a[key] !== 'string') {
          return 1;
        }

        if (typeof b[key] !== 'string') {
          return -1;
        }

        return a[key].localeCompare(b[key], [intl.locale], { caseFirst: 'upper' });
      });
      setData([...newData]);
      setCurrSort({ key, direction: 1 });
    },
    [data]
  );

  useEffect(() => {
    xsollaAnalytics.sendOpen('login_pa_module__user-attributes_open', 'open');
  }, []);

  if (fetching) {
    return (
      <Modal onClose={onClose} size="xl">
        <Preloader />
      </Modal>
    );
  }

  if (error || !data) {
    return (
      <Modal onClose={onClose} size="xl">
        <AttributesErrorMessage error={error} />
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} size="xl">
      <>
        <Header user={userData} data={data} setCurrFilterData={setCurrFilterData} />

        <AttributesTable
          userId={userData.id}
          tableData={data}
          sortStatuses={{
            order_column: currSort.key,
            order_dir: currSort.direction === 1 ? 'asc' : 'desc'
          }}
          currFilterData={currFilterData}
          sortFunction={sortFunction}
          setData={setData}
        />

        <Footer projectId={projectId} />
      </>
    </Modal>
  );
};

export default AttributesModal;
